// What a deal's tenants cost this month, and where the money went.
//
// THE DEAL IS THE SCOPE. A person asks what a customer costs, not what a tenant
// slug costs, and the deal is where the money already lives — so the cost
// screens hang off /deals/$dealId and the deal's bound tenants are the default
// scope (docs/crm-cost-explorer.md section 3).
//
// NOTHING HERE PRICES A FACT. The worker fills `crm.usage_daily_cost` and this
// screen reads it. When the rollup does not cover the month the report answers
// 202 with the job filling it and the PREVIOUS figures beside it, and this
// screen draws those under a progress bar rather than a spinner over an empty
// page: somebody who can already see roughly the right number can keep working.
//
// A CLOSED MONTH SAYS SO. Once a month's rated amounts are on a bill the report
// answers from those amounts and never re-evaluates, so the header reads
// "closed, as billed · run 3f9a…" and a rate corrected in October cannot change
// what September's screen says September cost.
//
// The one arithmetic this screen does is addition over disjoint parts: the
// report answers for ONE (deployment, tenant) pair, a deal binds several, and
// `usageMerge.ts` sums them — including the rule that two currencies are never
// added together.

import { useEffect, useMemo, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { Currency, UsageCostGroup, UsageCostResponse } from "@crm/shared";
import styles from "./UsagePage.module.css";
import { UsageJobProgress, UsageStatusLine } from "./UsageStatus.js";
import { mergeUsageCost, type UsageCostAnswer, type UsageCostUnion } from "./usageMerge.js";
import { formatUsageMoney, unpricedCount } from "./usageFormat.js";
import { resolveDeployments, isNonprod, writeStoredDeployments } from "./deploymentSelection.js";
import { bindingsInForce, deploymentsOf, scopeParts, tenantsOf } from "./usageScope.js";
import {
  periodToUsageSearch,
  searchToBreakdown,
  searchToSlugs,
  searchToUsagePeriod,
  shiftUsagePeriod,
  slugsToSearch,
  USAGE_BREAKDOWNS,
  type UsageBreakdown,
  type UsageSearch,
} from "./usageSearch.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { FilterSelect, MonthStepper, SelectionChips } from "../../design/Filters.js";
import { EmptyState, Notice, ProblemMessage, Spinner } from "../../design/Feedback.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { BreakdownBars } from "../../design/chart/BreakdownBars.js";
import { StackedBars, type StackedBar } from "../../design/chart/StackedBars.js";
import { colorForKey, labelForKey } from "../../design/chart/series.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { useDeal, useTenantBindings, useUsageCost, useUsageJob } from "../../api/queries.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import {
  daysInMonth,
  formatInstantDate,
  formatMonth,
  parseCalendarMonth,
  todayIsoDate,
} from "../../format/calendarDate.js";
import { formatCount } from "../../format/money.js";

const BREAKDOWN_OPTIONS = USAGE_BREAKDOWNS.map((value) => ({
  value,
  label: value === "model" ? "model" : value.replace("_", " "),
}));

export function UsageOverviewPage(): ReactNode {
  const { dealId } = useParams({ from: "/deals/$dealId/usage" });
  const deal = useDeal(dealId);
  const bindings = useTenantBindings(dealId);

  usePageChrome({
    title: deal.data === undefined ? "Usage cost" : `${deal.data.customer_name} · Usage cost`,
  });

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumb}>
        <Link to="/deals">Deals</Link>
        <span aria-hidden="true">/</span>
        <Link to="/deals/$dealId" params={{ dealId }}>
          {deal.data?.customer_name ?? "Deal"}
        </Link>
        <span aria-hidden="true">/</span>
        <span className={styles.crumbCurrent}>Usage</span>
      </div>

      <QueryBoundary query={bindings} label="this deal's tenants">
        {(list) =>
          list.items.length === 0 ? (
            <EmptyState
              title="No tenant is bound to this deal."
              description="Usage facts reach the CRM keyed by (deployment, tenant). Until a binding says which of those belong to this deal, there is nothing here to report — and nothing this deal spends can reach a bill."
            />
          ) : deal.data === undefined ? (
            // The figures come back in the deal's currency, so the reads wait
            // for the deal rather than ask twice.
            <Spinner label="Loading the deal…" />
          ) : (
            <UsageMonth dealId={dealId} bindings={list.items} currency={deal.data.currency} />
          )
        }
      </QueryBoundary>
    </div>
  );
}

interface UsageMonthProps {
  readonly dealId: string;
  readonly bindings: Parameters<typeof bindingsInForce>[0];
  /**
   * The deal's currency. This is one deal's page, so its costs read in the
   * deal's money, as its term lines and invoices do, whatever the report
   * currency is.
   */
  readonly currency: Currency;
}

function UsageMonth({ dealId, bindings, currency }: UsageMonthProps): ReactNode {
  const search = useSearch({ from: "/deals/$dealId/usage" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = todayIsoDate();
  const period = searchToUsagePeriod(search, today);
  const breakdown = searchToBreakdown(search);

  const inForce = useMemo(() => bindingsInForce(bindings, period), [bindings, period]);
  const availableDeployments = useMemo(() => deploymentsOf(inForce), [inForce]);
  const availableTenants = useMemo(() => tenantsOf(inForce), [inForce]);

  const deployments = resolveDeployments({
    dealId,
    available: availableDeployments,
    fromUrl: searchToSlugs(search.deployments),
  });
  const fromUrlTenants = searchToSlugs(search.tenants);
  const tenants =
    fromUrlTenants === undefined
      ? availableTenants
      : fromUrlTenants.filter((slug) => availableTenants.includes(slug));
  const selectedTenants = tenants.length === 0 ? availableTenants : tenants;

  const parts = useMemo(
    () => scopeParts(inForce, deployments, selectedTenants),
    [inForce, deployments, selectedTenants],
  );

  const dayResults = useUsageCost(
    parts.map((part) => ({
      period,
      groupBy: "day" as const,
      deploymentSlug: part.deploymentSlug,
      tenantSlug: part.tenantSlug,
      currency,
    })),
  );
  const breakdownResults = useUsageCost(
    parts.map((part) => ({
      period,
      groupBy: breakdown,
      deploymentSlug: part.deploymentSlug,
      tenantSlug: part.tenantSlug,
      currency,
    })),
  );

  const days = mergeUsageCost(answersOf(dayResults));
  const grouped = mergeUsageCost(answersOf(breakdownResults));

  // One job is polled, not all of them: they are refreshes of the same month,
  // they finish within seconds of each other, and the report is re-read when
  // any of them does.
  const jobId = days.jobs[0]?.job_id ?? grouped.jobs[0]?.job_id ?? null;
  const job = useUsageJob(jobId);
  const jobStatus = job.data?.status;
  useEffect(() => {
    if (jobStatus === "succeeded") {
      void queryClient.invalidateQueries({ queryKey: ["report", "usage-cost"] });
    }
  }, [jobStatus, queryClient]);

  const update = (next: Partial<UsageSearch>): void => {
    void navigate({
      to: "/deals/$dealId/usage",
      params: { dealId },
      search: (previous: UsageSearch) => ({ ...previous, ...next }),
    });
  };

  const toggleDeployment = (slug: string): void => {
    const next = deployments.includes(slug)
      ? deployments.filter((value) => value !== slug)
      : [...availableDeployments].filter((value) => value === slug || deployments.includes(value));
    // An empty chip row would report nothing at all, which reads as "this deal
    // costs nothing". The last chip on stays on.
    if (next.length === 0) return;
    writeStoredDeployments(dealId, next);
    update({ deployments: slugsToSearch(next) });
  };

  const toggleTenant = (slug: string): void => {
    const next = selectedTenants.includes(slug)
      ? selectedTenants.filter((value) => value !== slug)
      : [...availableTenants].filter((value) => value === slug || selectedTenants.includes(value));
    if (next.length === 0) return;
    update({ tenants: slugsToSearch(next) });
  };

  const pending = dayResults.some((result) => result.isPending) && days.report === null;
  const failed = dayResults.find((result) => result.error !== null)?.error ?? null;
  const includesNonprod = deployments.some(isNonprod);

  return (
    <>
      <UsageStatusLine
        report={days.report}
        period={period}
        onRefreshed={() => queryClient.invalidateQueries({ queryKey: ["report", "usage-cost"] })}
        deployments={deployments}
        tenants={selectedTenants}
      />

      <SelectionChips
        label="Deployments"
        options={availableDeployments.map((slug) => ({
          value: slug,
          label: slug,
          cautioned: isNonprod(slug),
        }))}
        selected={deployments}
        onToggle={toggleDeployment}
      />

      <div className={styles.controls}>
        <SelectionChips
          label="Tenants"
          options={availableTenants.map((slug) => ({ value: slug, label: slug }))}
          selected={selectedTenants}
          onToggle={toggleTenant}
        />
        <MonthStepper
          label={formatMonth(period)}
          onStep={(delta) =>
            update({ period: periodToUsageSearch(shiftUsagePeriod(period, delta), today) })
          }
        />
        <Link
          to="/deals/$dealId/usage/trend"
          params={{ dealId }}
          search={{ deployments: slugsToSearch(deployments) }}
        >
          12 months →
        </Link>
      </div>

      {days.jobs.length > 0 && <UsageJobProgress job={job.data ?? null} />}

      {failed !== null && <ProblemMessage error={failed} action="load this month's cost" />}

      {days.currencies.length > 1 && (
        <Notice tone="warn" title="These tenants are priced in more than one currency.">
          {`${days.currencies.join(" and ")} cannot be added together, so no total is shown. Read one deployment at a time until the meters agree on a currency.`}
        </Notice>
      )}

      {pending ? (
        <Spinner label="Loading this month's cost…" />
      ) : days.report === null || grouped.report === null ? (
        <EmptyState
          title="Nothing has been rolled up for this month yet."
          description="The rollup worker visits the open month every quarter of an hour. Refresh to queue a pass now; the figures appear when it finishes."
        />
      ) : (
        <UsageFigures
          days={days}
          grouped={grouped}
          breakdown={breakdown}
          period={period}
          includesNonprod={includesNonprod}
          onBreakdownChange={(next) => update({ group: next })}
        />
      )}
    </>
  );
}

interface UsageFiguresProps {
  readonly days: UsageCostUnion;
  readonly grouped: UsageCostUnion;
  readonly breakdown: UsageBreakdown;
  readonly period: string;
  readonly includesNonprod: boolean;
  readonly onBreakdownChange: (next: UsageBreakdown) => void;
}

function UsageFigures({
  days,
  grouped,
  breakdown,
  period,
  includesNonprod,
  onBreakdownChange,
}: UsageFiguresProps): ReactNode {
  const report = days.report;
  const groupedReport = grouped.report;
  if (report === null || groupedReport === null) return null;

  const mixedCurrency = days.currencies.length > 1;
  const unpriced = unpricedCount(report);
  const gaps = report.rate_gaps;
  const emphasis = days.stale ? styles.stale : undefined;

  return (
    <div className={emphasis}>
      <StatTileRow>
        <StatTile
          label="Total cost"
          value={mixedCurrency ? null : formatUsageMoney(report.cost_minor, report.currency)}
          hint={includesNonprod ? "includes nonprod · never billable" : `${report.period} to date`}
        />
        <StatTile
          label="Per call"
          // Null when nothing was called: an average over no calls is undefined,
          // and reporting it as zero says a call was free.
          value={
            report.cost_per_call_minor === null || mixedCurrency
              ? null
              : formatUsageMoney(report.cost_per_call_minor, report.currency)
          }
          hint="total divided by the calls counted"
        />
        <StatTile
          label="Calls"
          value={formatCount(report.calls)}
          hint="conversations in the month"
        />
        <StatTile
          label="Unpriced"
          // A COUNT of facts, never an amount: a fact nothing could price is not
          // a fact worth nothing.
          value={`${formatCount(unpriced)} facts`}
          hint={
            gaps.length === 0
              ? unpriced === 0
                ? "every fact was priced"
                : "no model is missing a rate; see the reasons below"
              : `${formatCount(gaps.length)} ${gaps.length === 1 ? "model has" : "models have"} no rate`
          }
        />
      </StatTileRow>

      <Card>
        <CardHeader
          title="Cost by day"
          description="One column a day, from the rollup the worker filled. A day with no facts is a day that cost nothing, and it is drawn as one."
        />
        <CardBody className={styles.cardBody}>
          <StackedBars
            bars={dayBars(report, period)}
            formatValue={(value) => formatUsageMoney(value, report.currency)}
            label={`Cost by day, ${formatMonth(period)}`}
            emptyMessage="No day of this month has a priced fact yet."
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Break down by"
          description="Sorted by cost. Every bar carries its figure, so nothing here is read by colour alone."
          actions={
            <FilterSelect
              label="Break down by"
              options={BREAKDOWN_OPTIONS}
              value={breakdown}
              onChange={onBreakdownChange}
            />
          }
        />
        <CardBody className={styles.cardBody}>
          <BreakdownBars
            rows={groupedReport.groups.map((group, index) => ({
              key: group.key,
              label: labelForKey(group.key),
              value: group.cost_minor,
              color: colorForKey(group.key, index),
              ...(hintFor(group) === null ? {} : { hint: hintFor(group) ?? undefined }),
            }))}
            formatValue={(value) => formatUsageMoney(value, groupedReport.currency)}
            total={groupedReport.cost_minor}
            label={`Cost by ${breakdown}`}
            emptyMessage="No fact in this month matched an active meter."
          />
          <div className={styles.phases}>
            {`AI leg ${formatUsageMoney(report.telephony_phases.ai_leg_cost_minor, report.currency)} / ${formatCount(Math.round(report.telephony_phases.ai_leg_seconds))} s · after handoff ${formatUsageMoney(report.telephony_phases.post_transfer_cost_minor, report.currency)} / ${formatCount(Math.round(report.telephony_phases.post_transfer_seconds))} s`}
            {report.telephony_phases.unsegmented_legs > 0 &&
              ` · ${formatCount(report.telephony_phases.unsegmented_legs)} carrier facts belong to neither phase`}
          </div>
        </CardBody>
      </Card>

      {gaps.length > 0 && (
        <Notice tone="warn" title="Some facts could not be priced, and they are not worth nothing.">
          <ul className={styles.gaps}>
            {gaps.map((gap) => (
              <li
                key={`${gap.deployment_slug} ${gap.provider} ${gap.operation} ${gap.model ?? ""}`}
              >
                <span className={styles.gapKey}>
                  {`${gap.provider} · ${gap.operation} · ${gap.model ?? "no model"}`}
                </span>
                {` — ${formatCount(gap.facts)} facts since ${formatInstantDate(gap.first_seen_at)}. A rate for it is entered on the rate card.`}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      <div className={styles.footer}>
        <span>{`Priced by ${formatCount(report.meters.length)} active ${report.meters.length === 1 ? "meter" : "meters"}: ${report.meters.join(", ")}`}</span>
        <span className={styles.footerSpacer} />
        <Link to="/billing/close" search={{ period }}>
          Open in the close →
        </Link>
      </div>
    </div>
  );
}

/** What a group is short of, said beside it rather than left to be discovered. */
function hintFor(group: UsageCostGroup): string | null {
  const reasons = group.unpriced_by_reason;
  const total = reasons.null_field + reasons.rate_unavailable + reasons.division_by_zero;
  if (total === 0) return null;
  const parts: string[] = [];
  if (reasons.rate_unavailable > 0) parts.push(`${reasons.rate_unavailable} with no rate`);
  if (reasons.null_field > 0) parts.push(`${reasons.null_field} with a field the meter needs`);
  if (reasons.division_by_zero > 0) parts.push(`${reasons.division_by_zero} that divide by zero`);
  return `${total} facts unpriced: ${parts.join(", ")}`;
}

/**
 * One bar per day of the month, in date order, zero-filled.
 *
 * The report groups on ONE dimension, so a day carries a total rather than a
 * stack per category — see the PR that added this screen. The primitive still
 * stacks; this caller has one band to give it.
 */
function dayBars(report: UsageCostResponse, period: string): StackedBar[] {
  const byDay = new Map(report.groups.map((group) => [group.key, group.cost_minor]));
  const month = parseCalendarMonth(period);
  const count = month === null ? 31 : daysInMonth(month);
  return Array.from({ length: count }, (_, index) => {
    const day = String(index + 1).padStart(2, "0");
    const key = `${period}-${day}`;
    return {
      key,
      label: key,
      // Every fifth day, so a month's axis does not become a smear.
      tick: index === 0 || (index + 1) % 5 === 0 ? String(index + 1) : "",
      segments: [
        {
          key: "cost",
          label: "cost",
          value: byDay.get(key) ?? 0,
          color: "var(--brand)",
        },
      ],
    };
  });
}

/** The answers that have arrived. A part still in flight contributes nothing yet. */
function answersOf(results: readonly { data: UsageCostAnswer | undefined }[]): UsageCostAnswer[] {
  return results.flatMap((result) => (result.data === undefined ? [] : [result.data]));
}
